/** Coalesce overlapping requests while preserving one trailing refresh. */
export function createTrailingRefresh(
  refresh: () => Promise<void>,
  signal?: AbortSignal
): () => Promise<void> {
  let inFlight: Promise<void> | null = null;
  let pending = false;

  const run = (): Promise<void> => {
    if (inFlight) {
      pending = true;
      return inFlight;
    }

    inFlight = refresh().finally(() => {
      inFlight = null;
      if (pending && !signal?.aborted) {
        pending = false;
        void run();
      }
    });
    return inFlight;
  };

  return run;
}

/**
 * Spreads a fleet-wide refresh signal across time. Every lobby client gets the
 * `lobby:v1:refresh` WS signal in the same instant, and without this each one
 * called `refresh()` immediately — a herd that dwarfs getLobby's
 * reservedConcurrency: 10 (EN-10108 scale review). Callers must invoke
 * `.cancel()` on unmount so a pending jittered call doesn't fire after the
 * component is gone.
 */
export function withJitter(
  refresh: () => void,
  maxDelayMs = 5000
): { trigger: () => void; cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return {
    trigger: () => {
      if (timeoutId !== null) return;

      timeoutId = setTimeout(() => {
        timeoutId = null;
        refresh();
      }, Math.random() * maxDelayMs);
    },
    cancel: () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }
  };
}
